import React, { useMemo, useState } from 'react';
import { Button, Form, FormInstance, Select } from 'antd';
import { EMetricTypes } from '@src/emp-3.0/types';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import EventItem from '@src/emp-3.0/components/Metrics/Flow/FlowForm/EventItem';
import { CalculateTypeOptions } from '../MetricDefinition';
import AttributeSelect from '@src/emp-3.0/components/custom-dimension/component/NamedSelectWithExtras/AttributeSelect';
import moment from 'moment';
import { SelectInView } from '../styled';
import { useGetEventNameById } from '@src/hooks/useGetEventNameById';
import { MetricRole, useGetEventToEdit } from './service';

interface GetMetricOptions {
  form: FormInstance;
  metricRole: MetricRole;
  isView?: boolean;
}
export const EventConfig: React.FC<GetMetricOptions> = ({ form, metricRole, isView }) => {
  const { eventToEdit, setEventToEdit } = useGetEventToEdit(metricRole);
  const eventId = Form.useWatch(['definition', metricRole, 'event'], form);
  const calculation = Form.useWatch(['definition', metricRole, 'calculation'], form);
  const attributeType = Form.useWatch(['definition', metricRole, 'attribute_type'], form);
  const { status: attributeStatus, enableRequired } = useFieldRequiredStatus([
    'definition',
    metricRole,
    'attribute',
  ]);

  // for get the event name by id
  const { getMappedEventNameById } = useGetEventNameById();
  const eventName = useMemo(() => {
    return getMappedEventNameById(eventId);
  }, [eventId, getMappedEventNameById]);
  const last14days = useMemo(
    () => [moment().subtract(13, 'days').startOf('day'), moment().subtract(0, 'day').endOf('day')],
    [],
  );

  const renderItemForViewMode = (form, item, options?) => {
    const getFieldValue = (fieldPath) => form.getFieldValue(fieldPath);
    const renderSelectInView = (value, style = {}) => (
      <SelectInView style={style}>{value}</SelectInView>
    );

    let value;
    let text;
    const metricsType = getFieldValue('metrics_type');

    switch (item) {
      case 'min_value':
      case 'max_value':
        value = getFieldValue(['definition', metricRole, 'distribution', item]);
        return renderSelectInView(value);

      case 'within_range':
        value = getFieldValue(['definition', metricRole, 'within_range']);
        return (
          <div style={{ marginTop: '-1px', display: 'inline-block', minWidth: '20px' }}>
            {renderSelectInView(value)}
          </div>
        );

      case 'is_higher_better':
        value = getFieldValue(['definition', metricRole, item]);
        text = options?.find((option) => option.value === value)?.label;
        if (metricsType === EMetricTypes.DURATION) {
          return renderSelectInView(text || value, { background: 'transparent' });
        }
        return renderSelectInView(text || value);

      default:
        value = getFieldValue(['definition', metricRole, item]);
        text = options?.find((option) => option.value === value)?.label;
        return renderSelectInView(text || value);
    }
  };
  return (
    <div>
      {eventId && (
        <div style={{ paddingLeft: '24px', marginBottom: '12px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                flexShrink: 0,
              }}
            >
              Calculate the
            </span>
            <Form.Item
              name={['definition', metricRole, 'calculation']}
              required={false}
              rules={[{ required: true }]}
              noStyle
            >
              {isView ? (
                renderItemForViewMode(form, 'calculation', CalculateTypeOptions)
              ) : (
                <Select
                  style={{ minWidth: '80px', margin: '0 8px', width: 'fit-content' }}
                  dropdownStyle={{ minWidth: '130px' }}
                  options={CalculateTypeOptions}
                />
              )}
            </Form.Item>
            <span>of</span>
            {calculation !== 'count' ? (
              <>
                {isView ? (
                  renderItemForViewMode(form, 'attribute')
                ) : (
                  <div style={{ width: '180px', margin: '0 5px' }}>
                    <Form.Item
                      name={['definition', metricRole, 'attribute']}
                      required={false}
                      validateStatus={attributeStatus}
                      rules={enableRequired ? [{ required: true, message: 'Required!' }] : []}
                      style={{ marginBottom: 0 }}
                    >
                      <AttributeSelect
                        eventId={eventId}
                        eventName={eventName}
                        interval={last14days}
                        attributeType={attributeType}
                        onAttributeTypeChange={(attributeType) =>
                          form.setFieldValue(
                            ['definition', metricRole, 'attribute_type'],
                            attributeType,
                          )
                        }
                      />
                    </Form.Item>
                  </div>
                )}
                <Form.Item
                  name={['definition', metricRole, 'attribute_type']}
                  dependencies={[['definition', metricRole, 'attribute']]}
                  style={{ display: 'none' }}
                />
                <span>for</span>
              </>
            ) : null}
            <span
              style={{
                margin: '0 5px',
                fontWeight: 500,
                color: `${eventName ? '#000' : 'rgba(0,0,0,.45)'}`,
              }}
            >
              {eventName || 'Event'}
            </span>
          </div>
        </div>
      )}
      <EventItem
        style={{ marginLeft: '24px' }}
        eventId={eventId}
        event={eventToEdit}
        viewModeProps={{
          onEdit: (event) => {
            setEventToEdit(event);
          },
          onDelete: () => {
            setEventToEdit(null);
            form.setFieldValue(['definition', metricRole, 'event'], undefined);
          },
          onClone: (event) => {
            setEventToEdit(event);
          },
        }}
        editModeProps={{
          onSave: (event) => {
            form.setFieldValue(['definition', metricRole, 'event'], event.event_id);
            setTimeout(() => {
              setEventToEdit(null);
            }, 100);
          },
          onCancel: () => {
            setEventToEdit(null);
          },
          onChange: (event) => {
            if (Number(event)) {
              form.setFieldValue(['definition', metricRole, 'event'], event);
              setTimeout(() => {
                setEventToEdit(null);
              }, 100);
            } else {
              setEventToEdit(event);
            }
          },
        }}
      />
    </div>
  );
};
